import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { apiClient } from '../services/api';
import { logger } from '../services/logger';
import type { AuthUser, LoginRequest, RegisterRequest } from '../types';
import { migrateLegacyPreferences } from '../lib/migratePreferences';
import { setUserDateDisplayMode, setUserLocale, setUserTimezone } from '../utils/dateFormat';
import { PREFERENCES_CHANGED_EVENT } from './LocaleContext';

interface AuthContextType {
  user: AuthUser | null;
  permissions: string[];
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  switchOrganization: (targetTenantId: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const extractPermissions = useCallback((authUser: AuthUser) => {
    return Array.isArray(authUser.permissions) ? authUser.permissions : [];
  }, []);

  const loadAndPersistUserTimezone = useCallback(async () => {
    try {
      const profile = await apiClient.getUserProfile();
      let changed = false;
      if (profile.timezone && profile.timezone.trim()) {
        setUserTimezone(profile.timezone);
        changed = true;
        logger.debug('User timezone loaded from profile', { timezone: profile.timezone });
      }
      if (profile.locale && profile.locale.trim()) {
        setUserLocale(profile.locale);
        changed = true;
      }
      if (
        profile.dateDisplayMode === 'relative' ||
        profile.dateDisplayMode === 'absolute' ||
        profile.dateDisplayMode === 'both'
      ) {
        setUserDateDisplayMode(profile.dateDisplayMode);
        changed = true;
      }
      if (changed && typeof window !== 'undefined') {
        // Notify LocaleContext to re-hydrate reactive state (no page reload needed).
        window.dispatchEvent(new Event(PREFERENCES_CHANGED_EVENT));
      }
    } catch (error) {
      logger.warn('Failed to load user profile preferences', { error });
    }
  }, []);

  const loadAuthenticatedUser = useCallback(async () => {
    const userData = await apiClient.validateToken();
    setUser(userData);
    setPermissions(extractPermissions(userData));
    await loadAndPersistUserTimezone();
    return userData;
  }, [extractPermissions, loadAndPersistUserTimezone]);

  const validateToken = useCallback(async () => {
    try {
      const userData = await loadAuthenticatedUser();
      logger.info('Token validated successfully', { userId: userData.userId });
    } catch (error) {
      logger.error('Token validation failed', error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadAuthenticatedUser]);

  useEffect(() => {
    // Validate token on mount
    const token = localStorage.getItem('auth_token');
    if (token) {
      logger.debug('Validating existing auth token');
      validateToken();
    } else {
      logger.debug('No auth token found');
      setIsLoading(false);
    }
  }, [validateToken]);

  useEffect(() => {
    if (!user?.tenantId || !user.userId) {
      return;
    }

    const migratedFlagKey = `cloudincidents_prefs_migrated_${user.tenantId}_${user.userId}`;
    if (localStorage.getItem(migratedFlagKey) === '1') {
      return;
    }

    migrateLegacyPreferences(user.tenantId, user.userId, apiClient)
      .then((result) => {
        if (result.views > 0 || result.columns > 0 || result.errors > 0) {
          logger.info('Legacy preferences migration completed', {
            tenantId: user.tenantId,
            userId: user.userId,
            ...result,
          });
        }
      })
      .catch((error) => {
        console.warn('Failed to migrate legacy preferences', {
          tenantId: user.tenantId,
          userId: user.userId,
          error,
        });
      });
  }, [user?.tenantId, user?.userId]);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await apiClient.login(credentials);
      localStorage.setItem('auth_token', response.token);
      if (response.refreshToken) {
        localStorage.setItem('refresh_token', response.refreshToken);
      } else {
        localStorage.removeItem('refresh_token');
      }
      const userData = await loadAuthenticatedUser();

      logger.info('Login successful', { userId: userData.userId, tenantId: userData.tenantId });
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setPermissions([]);
      logger.error('Login failed', error, { email: credentials.email });
      throw error;
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      const response = await apiClient.register(data);
      localStorage.setItem('auth_token', response.token);
      const userData = await loadAuthenticatedUser();

      logger.info('Registration successful', {
        userId: userData.userId,
        tenantId: userData.tenantId
      });
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setPermissions([]);
      logger.error('Registration failed', error, { email: data.email });
      throw error;
    }
  };

  const switchOrganization = async (targetTenantId: string) => {
    const response = await apiClient.switchTenant(targetTenantId);

    if (response.reauthRequired) {
      throw {
        reauthRequired: true,
        redirectUrl: response.redirectUrl,
        message: response.message || 'Re-authentication is required.',
      };
    }

    if (!response.token) {
      throw new Error(response.message || 'Failed to switch organization');
    }

    localStorage.setItem('auth_token', response.token);
    if (response.refreshToken) {
      localStorage.setItem('refresh_token', response.refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }

    await loadAuthenticatedUser();
    logger.info('Organization switched successfully', { targetTenantId });
    window.location.reload();
  };

  const logout = () => {
    logger.logAuth('User logout', { userId: user?.userId });
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setPermissions([]);
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    user,
    permissions,
    isLoading,
    login,
    register,
    switchOrganization,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider };
